import { z } from 'zod';

const GREENHOUSE_BOARD_URL =
  'https://boards-api.greenhouse.io/v1/boards/transcendinc/jobs?content=true';

const jobObjectSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  department: z.object({ id: z.string(), label: z.string() }),
  url: z.string(),
  workLocation: z.object({ id: z.string(), label: z.string() }),
});

export const jobSchema = z.array(jobObjectSchema);

export type Job = z.infer<typeof jobObjectSchema>;

const greenhouseJobSchema = z.object({
  jobs: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      absolute_url: z.string(),
      location: z.object({
        name: z.string(),
      }),
      metadata: z
        .array(
          z.object({
            name: z.string(),
            value: z.string(),
          }),
        )
        .nullable()
        .optional(),
      departments: z
        .array(
          z.object({
            id: z.number(),
            name: z.string(),
          }),
        )
        .optional(),
    }),
  ),
});

async function fetchGreenhouseJobs() {
  try {
    const response = await fetch(GREENHOUSE_BOARD_URL);

    if (!response.ok) {
      console.error(
        'Network response hitting Greenhouse was not ok',
        response.statusText,
      );
      return undefined;
    }

    const data = await response.json();
    const parsedData = greenhouseJobSchema.parse(data);

    // Map Greenhouse API response to match the expected schema
    const mappedJobs = parsedData.jobs.map((job) => {
      // Find department from departments array first, then fall back to metadata
      let departmentLabel = 'Other';

      if (job.departments && job.departments.length > 0) {
        // Use the first department's name
        departmentLabel = job.departments[0].name;
      } else if (job.metadata) {
        // Fall back to metadata if departments array is not available
        const departmentMetadata = job.metadata.find(
          (m) => m.name === 'Job Post Department',
        );
        departmentLabel = departmentMetadata?.value || 'Other';
      }

      const departmentId = departmentLabel.toLowerCase().replace(/\s+/g, '-');

      return {
        uuid: job.id.toString(),
        name: job.title,
        department: {
          id: departmentId,
          label: departmentLabel,
        },
        url: job.absolute_url,
        workLocation: {
          id: job.location.name.toLowerCase().replace(/\s+/g, '-'),
          label: job.location.name,
        },
      };
    });

    return jobSchema.parse(mappedJobs);
  } catch (err) {
    console.error('Error fetching from Greenhouse API: ', err);
    return null;
  }
}

export const fetchJobs = async () => {
  const greenhouseJobs = await fetchGreenhouseJobs();

  return greenhouseJobs ?? [];
};
